import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { FormsModule } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { UtworzDokumentDto } from '../../models/utworz-dokument-dto';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DokumentApi } from '../../services/api/dokument.api';
import { catchError, EMPTY, finalize, switchMap, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { HttpErrorResponse } from '@angular/common/http';
import { CheckboxModule } from 'primeng/checkbox';
import { Dokument } from '../../models/dokument';
import {
  HttpResponseErrorSource,
  HttpResponseService,
} from '../../../core/services/http-error-response.service';
import { CheckPossibilityToSubmitDokument } from '../../models/check-possibility-to-submit-dokument';

@Component({
  selector: 'app-korespondencja-dialog',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    RadioButtonModule,
    ProgressSpinnerModule,
    FormsModule,
    CheckboxModule,
  ],
  providers: [DokumentApi],
  templateUrl: './korespondencja-dialog.component.html',
  styleUrls: ['./korespondencja-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KorespondencjaDialogComponent implements OnInit {
  protected dokument = signal<Dokument | undefined>(undefined);
  protected typyWnioskow = signal<any[]>([]);
  protected selectedTyp = signal<any | null>(null);
  protected loading = signal(true);
  protected error = signal<string | null>(null);

  readonly #dialogRef = inject(DynamicDialogRef);
  readonly #documentApi = inject(DokumentApi);
  readonly #destroyRef = inject(DestroyRef);
  readonly #httpResponseService = inject(HttpResponseService);
  readonly #config = inject(DynamicDialogConfig);

  ngOnInit(): void {
    const docFromConfig = this.#config.data?.dokument;
    this.dokument.set(docFromConfig);

    const dokumentId = docFromConfig?.id;

    if (!dokumentId) {
      this.error.set(
        'Brak informacji o obszarze. Nie można pobrać typów dokumentów.',
      );
      this.loading.set(false);
      return;
    }

    this.#documentApi
      .getCorrespondenceDokumentTypesById(String(dokumentId))
      .pipe(
        tap((data) => {
          this.typyWnioskow.set(data);
          this.loading.set(false);
        }),
        catchError(() => {
          this.error.set('Nie udało się pobrać typów dokumentów.');
          return EMPTY;
        }),
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  protected createApplication(): void {
    const selected = this.selectedTyp();
    const dokument = this.dokument();

    if (!selected) {
      this.error.set('Nie wybrano typu dokumentu.');
      return;
    }

    if (!dokument) {
      this.error.set('Brak kontekstu dokumentu.');
      return;
    }

    const payload: UtworzDokumentDto = {
      czyDokumentTymczasowy: true,
      czyLiferay: false,
      czyKorespondencja: true,
      korespondencjaWSprawie: dokument.id,
      kodTypuDokumentu: selected.kod,
    };

    this.loading.set(true);

    this.#documentApi
      .checkPossibilityToSubmitDokument$(payload.kodTypuDokumentu!)
      .pipe(
        switchMap((status: CheckPossibilityToSubmitDokument) => {
          if (status.czyWniosekDostepny) {
            return this.#documentApi.createNewDokument$(payload);
          } else {
            return throwError(
              () => new Error('Wniosek jest obecnie niedostępny do złożenia.'),
            );
          }
        }),
        tap((response: UtworzDokumentDto) => {
          const baseUrl = environment.KREATORY_FE;
          const appCode = payload
            .kodTypuDokumentu!.replace('_', '')
            .toLowerCase();
          const { idJednostki, id } = response;

          window.location.href = `${baseUrl}/${appCode}/${idJednostki}/${id}`;
        }),
        catchError((err: HttpErrorResponse) => {
          this.#handleCreateNewDokumentError(err);
          return EMPTY;
        }),
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  protected cancel(): void {
    this.#dialogRef.close();
  }

  #handleCreateNewDokumentError(err: HttpErrorResponse): void {
    this.#httpResponseService.showErrorMessage(
      err,
      'Wystąpił błąd podczas próby utworzenia wniosku',
      HttpResponseErrorSource.MESSAGE_FROM_ERROR_CODE,
    );
  }
}
